import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import SEO, { siteUrl } from '../components/SEO';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

const Contact: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get('service') || '';

  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    service: requestedService,
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const services = [
    'InoviqSpace Demo Request',
    'Pixify Archi Walkthrough Demo Request',
    'DriveSkill VR Demo Request',
    'VR Walkthrough Demo Request',
    'Bathroom Configurator Demo Request',
    'Web Application',
    'Mobile App (iOS/Android)',
    'Game Development',
    'AR/VR Development',
    'Logo Design',
    'Website Design',
    'Other'
  ];

  useEffect(() => {
    if (!requestedService) {
      return;
    }

    setFormData((current) => ({
      ...current,
      service: requestedService
    }));
  }, [requestedService]);

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact RedRender',
    url: `${siteUrl}/contact`,
    description: 'Contact RedRender for web development, mobile apps, games, AR/VR, 3D visualization, virtual showrooms, and AI integration projects.',
    mainEntity: {
      '@type': 'Organization',
      name: 'RedRender',
      url: siteUrl,
      telephone: '+91 7990813060',
      email: 'info@redrender.in',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91 7990813060',
        contactType: 'sales',
        areaServed: 'Worldwide',
        availableLanguage: ['English', 'Hindi', 'Gujarati']
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitStatus('success');
      setSubmitMessage('Thank you! Your message has been sent successfully. We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
      
      setTimeout(() => {
        setSubmitStatus('idle');
        setSubmitMessage('');
      }, 5000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black pt-24 pb-20">
      <SEO
        title="Contact RedRender - Start Your Web, App, Game or AR/VR Project"
        description="Contact RedRender to discuss web development, mobile app development, game development, AR/VR, 3D visualization, virtual showroom, and AI integration projects."
        path="/contact"
        keywords={[
          'contact RedRender',
          'hire web developer',
          'hire app developer',
          'game development inquiry',
          'AR VR project quote',
          '3D visualization quote'
        ]}
        jsonLd={contactJsonLd}
      />
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:100px_100px] pointer-events-none"></div>
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Page Header */}
        <div className="text-center mb-16 sm:mb-20 animate-slide-up">
          <div className="inline-flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-full px-5 py-2 backdrop-blur-sm mb-6">
            <span className="text-red-500 font-semibold text-sm font-display">Contact Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display">
            Get In <span className="bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">Touch</span>
          </h1>
          <p className="text-gray-400 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Have a project in mind? Let's discuss how we can help bring your vision to life
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-8 animate-slide-up">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-display leading-tight">
                Let's Create Something <span className="text-red-500">Amazing Together</span>
              </h2>
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-8">
                Whether you have a question, need a quote, or want to discuss your next project, 
                we're here to help. Reach out and let's start the conversation.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="space-y-4">
              {[
                { Icon: FaEnvelope, title: 'Email Us', info: 'info@redrender.in', link: 'mailto:info@redrender.in' },
                { Icon: FaPhone, title: 'Call Us', info: '+91 799 081 3060', link: 'tel:+917990813060' },
                { Icon: FaMapMarkerAlt, title: 'Visit Us', info: 'India', link: null }
              ].map((contact, index) => (
                <div
                  key={index}
                  className="group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-red-500/50 transition-all duration-300 flex items-start space-x-4"
                >
                  <div className="flex-shrink-0 bg-red-500/20 p-4 rounded-xl group-hover:bg-red-500/30 transition-colors">
                    <contact.Icon className="text-red-500 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1 font-display">{contact.title}</h3>
                    {contact.link ? (
                      <a href={contact.link} className="text-gray-400 hover:text-red-400 transition-colors">
                        {contact.info}
                      </a>
                    ) : (
                      <p className="text-gray-400">{contact.info}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Business Hours */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-500/0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-red-500/30 transition-all duration-500">
                <h3 className="text-white font-semibold mb-4 font-display text-lg">Business Hours</h3>
                <div className="space-y-3 text-gray-400">
                  {[
                    { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM' },
                    { day: 'Saturday', hours: '10:00 AM - 4:00 PM' },
                    { day: 'Sunday', hours: 'Closed' }
                  ].map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center group/item">
                      <span className="group-hover/item:text-white transition-colors">{schedule.day}</span>
                      <span className="text-red-500 font-medium">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="relative group animate-scale-in">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-500/0 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
            <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-8 border border-white/10 hover:border-red-500/30 transition-all duration-500">
              <div onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-medium mb-2 font-display">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-300"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-medium mb-2 font-display">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-300"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white font-medium mb-2 font-display">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-300"
                      placeholder="+91 XXX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-white font-medium mb-2 font-display">
                      Service Interested In <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-300"
                    >
                      <option value="" className="bg-black">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service} className="bg-black">
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white font-medium mb-2 font-display">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all duration-300 resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="w-full relative px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-red-500/50 transition-all duration-300"
                  >
                    <span className="relative z-10 flex items-center justify-center space-x-2">
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <FaPaperPlane />
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </button>

                  {submitMessage && (
                    <div className={`flex items-center space-x-3 px-4 py-3 rounded-xl backdrop-blur-sm animate-slide-down ${
                      submitStatus === 'success' 
                        ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                        : 'bg-red-500/20 border border-red-500/50 text-red-400'
                    }`}>
                      {submitStatus === 'success' ? (
                        <FaCheckCircle className="text-2xl flex-shrink-0" />
                      ) : (
                        <FaExclamationCircle className="text-2xl flex-shrink-0" />
                      )}
                      <p>{submitMessage}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
